import { ref } from 'vue';
import { useVaultStore } from '@/stores/vaultStore';
import type { VaultFileInDb } from '@/api';

/**
 * Configuration options for file upload
 */
export interface FileUploadOptions {
  /**
   * Optional parent directory UID where the file should be uploaded
   */
  parentDirectoryUid?: string | null;
  /**
   * Upload straight into this contract instead of a folder; the file joins it
   * as a new contract file. Takes precedence over `parentDirectoryUid`.
   */
  contractUid?: string;
  /**
   * Optional callback function to track upload progress (0-100)
   */
  onProgress?: (progress: number) => void;
  /**
   * Chunk size in bytes (default: 3MB)
   */
  chunkSize?: number;
}

/**
 * Result of a file upload operation
 */
export interface FileUploadResult {
  /**
   * The UID of the uploaded file
   */
  fileUid: string;
  /**
   * The complete file object from the vault
   */
  file: VaultFileInDb;
  /**
   * The UID of the revision this upload created
   */
  revisionUid: string;
}

/**
 * Return type for the useFileUpload composable
 */
export interface UseFileUploadReturn {
  /**
   * Reactive ref indicating if a file is currently being uploaded
   */
  isUploading: ReturnType<typeof ref<boolean>>;
  /**
   * Reactive ref tracking upload progress (0-100)
   */
  progress: ReturnType<typeof ref<number>>;
  /**
   * Reactive ref containing error message if upload fails
   */
  error: ReturnType<typeof ref<string | null>>;
  /**
   * Function to upload a file to the vault
   */
  uploadFile: (file: File, options?: FileUploadOptions) => Promise<FileUploadResult>;
  /**
   * Function to reset upload state
   */
  reset: () => void;
}

/**
 * Composable for handling chunked file uploads to the vault
 *
 * This composable provides a reusable way to upload files to the vault
 * using chunked uploads. It handles initialization, chunk uploading,
 * progress tracking, and finalization.
 *
 * @example
 * ```ts
 * const { uploadFile, isUploading, progress, error } = useFileUpload();
 *
 * const result = await uploadFile(file, {
 *   parentDirectoryUid: 'some-uid',
 *   onProgress: (p) => console.log(`Progress: ${p}%`)
 * });
 * ```
 */
export function useFileUpload(): UseFileUploadReturn {
  const vaultStore = useVaultStore();

  // Default chunk size: 3MB
  const DEFAULT_CHUNK_SIZE = 1024 * 1024 * 3;

  // State
  const isUploading = ref<boolean>(false);
  const progress = ref<number>(0);
  const error = ref<string | null>(null);

  /**
   * Upload a file to the vault using chunked upload
   *
   * @param file - The file to upload
   * @param options - Upload configuration options
   * @returns Promise that resolves with the uploaded file UID and file object
   * @throws Error if upload fails
   */
  async function uploadFile(
    file: File,
    options: FileUploadOptions = {}
  ): Promise<FileUploadResult> {
    // Reset state
    isUploading.value = true;
    progress.value = 0;
    error.value = null;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
    const chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE;

    try {
      // Create empty file anchor
      const vaultFile = await vaultStore.createEmptyFile(
        options.contractUid
          ? { contractUid: options.contractUid }
          : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
            options.parentDirectoryUid || undefined
      );

      // Initialize revision upload
      const uploadId = await vaultStore.initializeRevisionUpload(vaultFile.uid, {
        file_name: file.name,
        total_bytes: file.size,
      });

      // Calculate total chunks
      const totalChunks = Math.ceil(file.size / chunkSize);

      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * chunkSize, (i + 1) * chunkSize);

        await vaultStore.uploadFileChunk(uploadId, chunk, i, totalChunks);

        const currentProgress = Math.round(((i + 1) / totalChunks) * 100);
        progress.value = currentProgress;

        if (options.onProgress) {
          options.onProgress(currentProgress);
        }
      }

      // Finalize revision upload
      const revision = await vaultStore.finalizeRevisionUpload(vaultFile.uid, uploadId);

      progress.value = 100;
      if (options.onProgress) {
        options.onProgress(100);
      }

      return {
        fileUid: vaultFile.uid,
        file: vaultFile,
        revisionUid: revision.uid,
      };
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : 'Unknown upload error';
      error.value = errorMessage;
      throw new Error(`Failed to upload file "${file.name}": ${errorMessage}`);
    } finally {
      isUploading.value = false;
    }
  }

  /**
   * Reset upload state
   */
  function reset(): void {
    isUploading.value = false;
    progress.value = 0;
    error.value = null;
  }

  return {
    // State
    isUploading,
    progress,
    error,

    // Methods
    uploadFile,
    reset,
  };
}
